import { inject, Injectable } from '@angular/core';
import { forEach, get, includes, replace, set, startsWith, toArray } from 'lodash-es';

import { WmsWorkspaceManagerService } from '@wms/workspace';
import { runtimeEventManager } from "@wm/core";
import { EventManagerService } from "@wms/app";
import { setWidgetRegistry } from "@wm/components-base";
import { ComponentType } from "@wms/design-container";

// TODO: move this to some common file in Base.js
let WIDGET_CONSTANTS = {
  //This list will be extended with variable types in baseService
  EVENTS: {
    'NO_EVENT': 'No Event',
    'JAVASCRIPT': 'Javascript',
    'STOP_PROPAGATION': 'Stop Propagation',
    'STOP_PROP_FN': '$event.stopPropagation()'
  },
  EVENTS_OPTIONS: ['No Event', 'Javascript', 'Stop Propagation'],
  NAVIGATION_TYPE: {
    'BASIC': 'Basic',
    'CLASSIC': 'Classic',
    'ADVANCED': 'Advanced',
    'SCROLL': 'Scroll',
    'INLINE': 'Inline',
    'PAGER': 'Pager',
    'ONDEMAND': 'On-Demand'
  }
};

@Injectable({
  providedIn: 'root'
})
export class MarkupManagerService {
  private activeCanvasEl: JQuery<HTMLElement> | undefined | null;
  private treeRoot: JQuery<HTMLElement> | undefined;
  private canvasRoot: JQuery<HTMLElement> | undefined;
  private activeTreeEl: JQuery<HTMLElement> | undefined;
  private activeMarkupEl: JQuery<HTMLElement> | undefined | null;
  private markup: JQuery<HTMLElement> | undefined;
  private idMap: { [key: string]: number } = {};
  private wsManager: WmsWorkspaceManagerService;
  private activePageName: string;
  private eventManager: EventManagerService;
  processedMarkupForDialog: JQuery<HTMLElement>;

  constructor() {
    this.wsManager = inject(WmsWorkspaceManagerService);
    runtimeEventManager.setService('markupManager', this);
    this.eventManager = inject(EventManagerService);

    this.eventManager.selectPageObservable().subscribe((pageName: string) => {
      this.activePageName = pageName;
    });
  }

  // TODO write subscribe to event
  // $rs.$on('set-markup-attr', function (event, widgetId, attrs, childSelector) {
  //   event.stopPropagation();
  //   setAttr(widgetId, attrs, childSelector);
  // });
  //
  // $rs.$on('wms:migrate-box-model-props', function (e, widgetId, obj) {
  //   e.stopPropagation();
  //
  //   var el = markup.find('[widgetid=' + widgetId + ']');
  //
  //   _.forEach(obj, function (v, k) {
  //     var suffix = '';
  //     if (k === 'border') {
  //       suffix = 'width';
  //     }
  //     el.attr(k + suffix, v);
  //   });
  //
  //   el.removeAttr(boxModelDeprecatedAttrs);
  // });

  setAttr(widgetId: string, attrs: Attr, childSelector: string) {
    //If a child selector is passed, find and update the attributes for the child element else set attributes for the widget
    if (childSelector) {
      this.markup?.find('[widget-id=' + widgetId + '] ' + childSelector).attr(attrs);
      //Save the markup after updating the attributes
      this.wsManager.saveWsEvent.next('save from markup manager');
    } else {
      this.markup?.find('[widget-id=' + widgetId + ']').attr(attrs);
    }
  }

  setMarkup(arg: JQuery<HTMLElement>) {
    this.markup = arg;
    this.prependPageNameMarker();
  }

  setMarkupRoot(markupStr: string) {
    markupStr = '<div widgetid="markuproot" widgettype="markuproot">' + markupStr + '</div>';
    this.markup = $(markupStr);
    this.prependPageNameMarker();
  }

  processMarkupForDialogs(markupStr: string, componentName: string, componentType: ComponentType) {
    let markup = $(markupStr);
    let elements = markup[0].querySelectorAll('[wmdialog]');
    this.assignDeferLoad(elements);
    if (componentType === ComponentType.PAGE) {
      this.processedMarkupForDialog = markup;
    } else if (componentType === ComponentType.PARTIAL) {
      this.processedMarkupForDialog.find('[content="' + componentName + '"]').append(markup);
    }
    // TODO for prefab also
    return markup[0].innerHTML;
  }

  processMarkupForFullHeightNav() {
   let leftPanelEl = this.markup?.find('wm-left-panel');
   let topNavEl = this.markup?.find('wm-top-nav');
   let headerEl = this.markup?.find('wm-header');
   let footerEl = this.markup?.find('wm-footer');
   let pageEl = this.markup?.find('wm-page');
   let contentEl = this.markup?.find('wm-content');
   //remove left panel, header and footer from markup
   this.markup?.remove('wm-left-panel');
   this.markup?.remove('wm-top-nav');
   this.markup?.remove('wm-header');
   this.markup?.remove('wm-footer');
   //prepend left panel as first element in page
   leftPanelEl && pageEl?.prepend(leftPanelEl);
   topNavEl && contentEl?.prepend(topNavEl);
   //prepend header as first element in content
   headerEl && contentEl?.prepend(headerEl);
   //append footer as last element in content
   footerEl&& contentEl?.append(footerEl);
  }

  processMarkupForClippedNav() {
   let leftPanelEl = this.markup?.find('wm-left-panel');
   let topNavEl = this.markup?.find('wm-top-nav');
   let headerEl = this.markup?.find('wm-header');
   let footerEl = this.markup?.find('wm-footer');
   let pageEl = this.markup?.find('wm-page');
   let contentEl = this.markup?.find('wm-content');
   //remove left panel, header and footer from markup
   this.markup?.remove('wm-left-panel');
   this.markup?.remove('wm-top-nav');
   this.markup?.remove('wm-header');
   this.markup?.remove('wm-footer');
   topNavEl && pageEl?.prepend(topNavEl);
   //prepend header as first element in page
   headerEl && pageEl?.prepend(headerEl);
   //prepend left panel as first element in content
   leftPanelEl && contentEl?.prepend(leftPanelEl);
   //append footer as last element in page
   footerEl&& pageEl?.append(footerEl);
  }

  assignDeferLoad(elements: any) {
    // Loop through each element
    forEach(elements, (element: any) => {
      setWidgetRegistry(element, this.activePageName);
      element.children.length && this.assignDeferLoad(element.children);
    });
  }
  prependPageNameMarker() {
    if (this.activePageName) {
      if (this.markup?.attr('widgettype') === "markuproot")
        this.markup?.attr('pageName', this.activePageName);
    }
  }

  getMarkup(): JQuery<HTMLElement> | undefined {
    return this.markup;
  }

  setCanvasRoot(element: JQuery<HTMLElement>) {
    this.canvasRoot = element;
  }

  setTreeRoot(element: JQuery<HTMLElement>) {
    this.treeRoot = element;
  }

  getUniqueIdByType(type: string, parentId?: string, parentType?: string, isSkipCounter?: boolean) {
    if (parentId && parentType) {
      type = type.replace(parentType, parentId);
    } else {
      type = type.replace(/-/g, '_');
    }
    if (!(this.idMap)[type]) {
      this.idMap[type] = 0;
    }
    this.idMap[type] = this.idMap[type] + 1;
    return isSkipCounter ? type : type + this.idMap[type];
  }

  resetIdMap() {
    this.idMap = {};
  }

  resetIdMapByType(type: string) {
    /*Iterate through the idMap and reset the count for all types containing the specified type.*/
    // @ts-ignore,  as we need to break the loop
    forEach(this.idMap, (widgetCount: number, widgetType: string) => {
      if (includes(widgetType, type)) {
        set(this.idMap, widgetType, 0);
        return false;
      }
    });
  }

  decrementIdMapByType(type: string) {
    //check for parameter sanity
    if (type === undefined || type === '') {
      return;
    }
    const id = get(this.idMap, type);
    // check for the idMap sanity
    if (!id || id === 0) {
      return;
    }

    //decrement the id counter for widget type
    set(this.idMap, type, id - 1);
  }

  // This function returns the cleaned markup by removing unnecessary attributes
  getCleanedMarkup(): string {
    // for decorated markup, create a clone of the original markup.
    let clonedMarkup = this.markup?.clone(true),
      // element attributes which are to be removed
      toBeRemovedAttrs = ['widgettype', 'widgetid', 'widget-id', 'candelete', 'wm-resizable', 'wm-widget-overlay', 'is-container', 'movable-in-canvas',
        'is-template-bundled', 'is-layout-bundled', 'show-match', 'show-count', 'show-collapsible', 'show-date-format', 'wm-widget-overlay-drag', 'wm-widget-drag',
        'wm-droppable', 'wm-droppable-parent', 'part', 'newcolumns', 'scaffold', 'is-widget-listed'],
      // element attributes which should be included while adding markup attributes for null values
      emptyValAttrs = ['placeholder', 'caption', 'nodatamessage', 'loadingdatamsg', 'insertmessage', 'updatemessage', 'confirmdelete', 'deletemessage', 'errormessage', 'gridclass', 'postmessage', 'title', 'loadingicon', 'maxplaceholder', 'iconclass'];

    clonedMarkup?.find('*').each(function () {
      let element = $(this),
        attrList,
        attrs,
        ele;
      ele = element[0];
      let hasWidgetId = element.attr('widget-id') || element.attr('widgetid');
      if (!hasWidgetId) {
        return;
      }

      attrList = ele.attributes;
      attrs = toBeRemovedAttrs.concat();

      toArray(attrList).forEach(function (attr) {
        let key = attr.name, value = attr.value.trim();
        if (value === '' && !(includes(emptyValAttrs, key))) {
          attrs.push(key);
        }
        // remove the event related attributes having value as 'No Event'
        if (value === WIDGET_CONSTANTS.EVENTS.NO_EVENT && startsWith(key, 'on-')) {
          attrs.push(key);
        }
      });
      if (ele.getAttribute('tabindex') === '0') {
        attrs.push('tabindex');
      }
      if (ele.getAttribute('autoplay') === 'false') {
        attrs.push('autoplay'); //'autoplay' attribute should be removed to disable it.
      }
      if (ele.getAttribute('loop') === 'false') {
        attrs.push('loop'); //'loop' attribute should be removed to disable it.
      }
      if (ele.getAttribute('multiple') === 'false') {
        attrs.push('multiple'); //'multiple' attribute should be removed to disable it.
      }

      element.removeAttr(attrs.join(' '));
    });
    return clonedMarkup?.html() || '';
  }

  getDecoratedMarkup() {
    return this.getCleanedMarkup().replace(/>\s+</g, '>\n<').replace(/>\s+<\//g, '></');
  }

  setActiveMarkupEl(element: JQuery<HTMLElement> | null) {
    this.activeMarkupEl = element;
  }

  setActiveCanvasEl(element: JQuery<HTMLElement> | null) {
    this.activeCanvasEl = element;
  }

  setActiveTreeEl(element: JQuery<HTMLElement>) {
    this.activeTreeEl = element;
  }

  getActiveMarkupEl() {
    return this.activeMarkupEl;
  }

  getActiveCanvasEl() {
    return this.activeCanvasEl;
  }

  getActiveTreeEl() {
    return this.activeTreeEl;
  }

  getActivePageName() {
    return this.activePageName;
  }
  /**
   * - Inserts a <wm-container> at the bottommost selected element's position and moves selected nodes into it (preserving document order).
   */
  wrapWidgetsIntoContainer(payload: { newWidgetId: string, name?: string, parentWidgetId?: string | null, childrenWidgetIds: string[] }) {
    if (!this.markup) {
      return;
    }
    const { newWidgetId, name, parentWidgetId, childrenWidgetIds } = payload;

    let $parent;
    if (parentWidgetId) {
      $parent = this.markup?.find('[widgetid="' + parentWidgetId + '"], [widget-id="' + parentWidgetId + '"]');
    } else {
      $parent = this.markup;
    }
    if (!$parent || !$parent.length) {
      return;
    }

    const directChildren = $parent.children('[widgetid], [widget-id]').toArray();

    const selectedSet = new Set(childrenWidgetIds);
    const selectedNodes: HTMLElement[] = [];
    for (const el of directChildren) {
    const jq = $(el) as JQuery<HTMLElement>;
    const id = jq.attr('widgetid') || jq.attr('widget-id');
    if (!id) continue; // SKIP elements without id
    if (selectedSet.has(id)) {
      selectedNodes.push(el as HTMLElement);
    }
  }

    if (!selectedNodes.length) {
      const allCandidates = $parent.find('[widgetid], [widget-id]').toArray();
      for (const el of allCandidates) {
        const jq = $(el) as JQuery<HTMLElement>;
        const id = jq.attr('widgetid') || jq.attr('widget-id');
        if (!id) continue;
        if (selectedSet.has(id)) {
          selectedNodes.push(el as HTMLElement);
        }
      }
    }

    if (!selectedNodes.length) return;

    const anchor = selectedNodes[selectedNodes.length - 1];
    const $anchor = $(anchor);
    const $container = $('<wm-container></wm-container>');
    $container.attr('widgetid', newWidgetId);
    $container.attr('widget-id', newWidgetId);
    $container.attr('widgettype', 'wm-container');
    if (name) $container.attr('name', name);

    $anchor.before($container);

    for (const node of selectedNodes) {
      if (!document.documentElement.contains(node)) continue;
      if ($container[0].contains(node)) continue;
      $container.append(node); // moves node into container
    }

    this.prependPageNameMarker();
    this.assignDeferLoad([$container.get(0)]); // register new nodes

    try {
      this.wsManager && this.wsManager.saveWsEvent && this.wsManager.saveWsEvent.next('autolayout-added');
    } catch (err) {
      runtimeEventManager.emit('markup-updated', { reason: 'autolayout-added', newWidgetId, parentWidgetId, childrenWidgetIds });
    }
  }
}
